<template>
    <!-- MYAC-1313 — the pre-generated opening message: rendered from
         `views/pilotage_opening`, NEVER generated here (no model call at load is
         the contract, not an optimisation). Drawn by ModelMessage itself, so it
         looks exactly like any assistant turn (review 2026-09-14: no bubble, no
         sender line — the AI label sits in the chat header). It writes itself in
         as if just received, then its reply chips appear. -->
    <div class="flex flex-col gap-2 w-full">
        <!-- Until the view answers: a skeleton where the message will land. The
             generic welcome flashing first, then swapped out, read as a glitch
             (review 2026-09-14). -->
        <div v-if="pending" class="opening-skeleton flex flex-col gap-2 w-full px-1" aria-busy="true"
            data-testid="opening-skeleton">
            <Skeleton width="92%" height="0.9rem" />
            <Skeleton width="70%" height="0.9rem" />
            <div class="flex flex-wrap gap-2 mt-2">
                <Skeleton width="8rem" height="2rem" border-radius="999px" />
                <Skeleton width="10rem" height="2rem" border-radius="999px" />
                <Skeleton width="9rem" height="2rem" border-radius="999px" />
            </div>
        </div>
        <template v-else>
            <ModelMessage class="chat-opening-message w-full" :message="asMessage"
                :avatar-icon="avatarIcon" :assistant-name="assistantName" />
            <!-- The reply chips: each one OPENS THE CONVERSATION — clicking sends its
                 own text to the chat, exactly as shown, nothing else. A send is a paid chat message, so
                 the chips share the input's credit gate (`$$$` rule: same block as
                 ChatInterface's own suggestion chips). They appear once the message
                 has finished writing itself, and once something is sent they are
                 spent: a lingering opening keeps its message, not its chips. -->
            <!-- Redesign: replies are lines with an arrow (the welcome letter's
                 shape), not pills — read down, not scanned across. -->
            <div v-if="replies.length && !lingering && !typing"
                class="flex flex-col border-t border-surface-200 chat-opening-arrive">
                <!-- The first reply accepts the offer the message ends on — it is
                     the one to press, so it is the highlighted one. -->
                <template v-for="(reply, i) in replies" :key="i">
                    <!-- MYAC-1522 (lot 3): a reply that leads to a page is a link — free,
                         never credit-gated, new-tab friendly (front-ui §3). -->
                    <RouterLink v-if="reply.to" :to="reply.to" class="opening-reply" :class="{ 'is-first': i === 0 }"
                        data-testid="opening-reply-link">
                        <span>{{ reply.label }}</span>
                        <i class="pi pi-arrow-right text-sm" aria-hidden="true" />
                    </RouterLink>
                    <button v-else type="button"
                        class="opening-reply" :class="{ 'is-first': i === 0 }"
                        :disabled="creditsBlocked"
                        @click="emit('reply', reply)">
                        <span>{{ reply.label }}</span>
                        <i class="pi pi-arrow-right text-sm" aria-hidden="true" />
                    </button>
                </template>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import Skeleton from 'primevue/skeleton'
import type { RouteLocationRaw } from 'vue-router'
import ModelMessage from '@/components/chat/ModelMessage.vue'
import type { ChatbotMessage } from '@/stores/chatbot'

/**
 * One chip, already locale-resolved by the host. Its label is also what it sends:
 * what the user sees is what the chat receives (review 2026-09-14).
 */
export interface OpeningReplyChip {
    label: string
    kind: string
    /** A page to open instead of a message to send (MYAC-1522 lot 3). */
    to?: RouteLocationRaw
    /**
     * The host's own gesture instead of a message sent in this chat: a generated
     * welcome letter's reply opens its project's chat (MYAC-1522, B3).
     */
    run?: () => void
}

const props = withDefaults(defineProps<{
    message: string
    replies: OpeningReplyChip[]
    /** The view has not answered yet: a skeleton holds the place. */
    pending?: boolean
    creditsBlocked?: boolean
    /** The host's persona — the same name and avatar its assistant turns carry. */
    assistantName?: string
    avatarIcon?: string
    /**
     * The conversation has started and this is now turn zero of the transcript:
     * keep the message (whole, no typing), drop its chips (spent once something is
     * sent — review 2026-09-14).
     */
    lingering?: boolean
}>(), {
    pending: false,
    creditsBlocked: false,
    assistantName: '',
    avatarIcon: '',
    lingering: false,
})

const emit = defineEmits<{
    reply: [reply: OpeningReplyChip]
}>()

/**
 * The message writes itself in, as if just received (review 2026-09-14): the whole
 * text over TYPE_MS whatever its length, then the chips appear. Not when lingering
 * (already read), and not for a reader who asked for reduced motion.
 */
const TYPE_MS = 1200
const TICK_MS = 30
const typed = ref(0)
let timer: ReturnType<typeof setInterval> | null = null

function stopTyping(): void {
    if (timer) clearInterval(timer)
    timer = null
}

function prefersReducedMotion(): boolean {
    return typeof window !== 'undefined'
        && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true
}

function startTyping(): void {
    stopTyping()
    const total = props.message.length
    if (props.pending) {
        typed.value = 0
        return
    }
    if (props.lingering || prefersReducedMotion() || total === 0) {
        typed.value = total
        return
    }
    typed.value = 0
    const step = Math.max(1, Math.ceil(total / (TYPE_MS / TICK_MS)))
    timer = setInterval(() => {
        typed.value = Math.min(total, typed.value + step)
        if (typed.value >= total) stopTyping()
    }, TICK_MS)
}

watch(() => [props.message, props.pending], startTyping, { immediate: true })
onBeforeUnmount(stopTyping)

const typing = computed(() => typed.value < props.message.length)

/** The opening as one model turn, so ModelMessage draws it like any other. */
const asMessage = computed<ChatbotMessage>(() => ({
    id: 'pilotage-opening',
    parts: [{ text: props.message.slice(0, typed.value) }],
    role: 'model',
    isUserMessage: false,
    isStreaming: false,
    // ModelMessage's own streaming look (typing caret) while it writes itself in.
    isPartial: typing.value,
    timestamp: 0,
}))
</script>

<style scoped>
/* The reply lines (`.opening-reply`) are styled in assets/main.css: the chat's
   clarifying questions draw their replies the same way (MYAC-1522). */
/* The light arrival animation of the reply chips, once the message is written.
   Subtle on purpose: the reader it exists for is already anxious. */
.chat-opening-arrive {
    animation: opening-arrive 0.45s ease-out both;
}

@keyframes opening-arrive {
    from {
        opacity: 0;
        transform: translateY(8px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@media (prefers-reduced-motion: reduce) {
    .chat-opening-arrive {
        animation: none;
    }
}
</style>
